'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';

import { Badge } from '@/components/badge';
import { WORD_COUNTER_FAQS } from './faqs';
import { computeTextStats, formatDurationShort } from '@engine/tools-shell';
import { useLocalToolRun } from '@/components/free-tools/local-tool-runs';
import { snippetTitle } from '@/lib/free-tools/local-tool-runs';

export function TranscriptWordCounterClient() {
  const [input, setInput] = useState('');
  const stats = useMemo(() => computeTextStats(input), [input]);

  useLocalToolRun(
    input.trim()
      ? {
          title: snippetTitle(input),
          summary: `${stats.words.toLocaleString()} words · ${formatDurationShort(stats.readingSeconds)} read`,
          state: { input },
        }
      : null,
    (state) => setInput(typeof state.input === 'string' ? state.input : '')
  );

  const cards: Array<{ label: string; value: string }> = [
    { label: 'Words', value: stats.words.toLocaleString() },
    { label: 'Characters', value: stats.characters.toLocaleString() },
    { label: 'Characters (no spaces)', value: stats.charactersNoSpaces.toLocaleString() },
    { label: 'Sentences', value: stats.sentences.toLocaleString() },
    { label: 'Paragraphs', value: stats.paragraphs.toLocaleString() },
    { label: 'Reading time', value: formatDurationShort(stats.readingSeconds) },
    { label: 'Speaking time', value: formatDurationShort(stats.speakingSeconds) },
  ];

  return (
    <div className="space-y-6">
      {/* H1 and intro live in FreeToolPageShell; only live status stays here. */}

      <div className="rounded-[28px] border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 sm:p-6 grid gap-6 md:grid-cols-[1fr_280px]">
        <textarea
          aria-label="Transcript or text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={18}
          placeholder="Paste your transcript or text here..."
          className="min-h-52 w-full resize-y rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 text-sm leading-6"
        />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
          {cards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-4"
            >
              <p className="text-xs text-[rgb(var(--foreground-secondary))]">{card.label}</p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{card.value}</p>
            </div>
          ))}
        </div>
      </div>

      <details className="text-sm text-[rgb(var(--foreground-secondary))]">
        <summary>About this tool</summary>{' '}
        <div className="space-y-4">
          <p className="text-[rgb(var(--foreground-secondary))]">
            Paste a transcript or any text to get an instant word count, character count, and
            estimated reading and speaking time. Runs entirely in your browser. Free, no signup.
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Need a transcript first?{' '}
            <Link href="/tools/free-audio-to-text" className="underline underline-offset-2">
              Transcribe audio to text
            </Link>{' '}
            free.
          </p>
        </div>
      </details>
    </div>
  );
}

/**
 * Long-form SEO cards for this tool. Rendered by the page through FreeToolPageShell's `body`
 * slot, below the hero, so the tool, its "About"/"How this works" panel and the platform
 * chips stay together in the first view.
 */
export function TranscriptWordCounterSeoSections() {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">How the estimates work</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            Reading time assumes a silent reading speed of ~200 words per minute. Speaking time
            assumes ~130 words per minute, a typical narration/podcast pace, handy for scripting
            videos and estimating episode length.
          </p>
        </section>
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">Private by design</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            Your text never leaves your browser. Nothing is uploaded or stored. Count as much as you
            like, free.
          </p>
        </section>
      </div>

      {/* Soft upsell: the only conversion surface on this client-side tool. */}
      <div className="mt-12">
        <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-r from-fuchsia-500/10 via-violet-500/10 to-amber-500/10 p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div className="space-y-1">
            <p className="text-base font-semibold text-[rgb(var(--foreground))]">
              Have audio or video instead of text?
            </p>
            <p className="text-sm text-[rgb(var(--foreground-secondary))]">
              Transcribe a recording free in minutes and count words, plus generate a summary, show
              notes, or chapters from the same upload.
            </p>
          </div>
          <Link
            href="/tools/free-audio-to-text"
            className="mt-3 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-95 sm:mt-0"
          >
            Transcribe free →
          </Link>
        </div>
      </div>
    </>
  );
}
